import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { OAuthService, AuthConfig } from 'angular-oauth2-oidc';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { App } from '@capacitor/app';
import { filter } from 'rxjs/operators';

import { environment } from '../environments/environment';

// Natív (iOS/Android) OAuth-visszairányítás EGYEDI URL-sémával (custom scheme), NEM https universal linkkel.
// A custom scheme megbízhatóan visszahív az appba szerveroldali apple-app-site-association / assetlinks.json
// nélkül is – ezt kell whitelistelni a Keycloak kliensnél (Valid Redirect URIs + Valid Post Logout Redirect URIs).
// FONTOS: nem a 'sulinform' sémát használjuk, mert azt a capacitor.config iosScheme a WebView origójaként foglalja.
// Több-app matrix: a scheme = az app appId-je, ami a fixált alrendszertől függ (hu.cronomit.sulinform /
// .naplo / .ber / .caesar). Üres fixedMode → sulinform (visszafelé kompatibilis). A capacitor.config
// ugyanezt a formulát használja az appId-hez, és app-onként whitelistelni kell a Keycloak IdP-n.
const APP_MODE = String((environment as any).fixedMode || 'SULINFORM').toLowerCase();
const NATIVE_REDIRECT_SCHEME = 'hu.cronomit.' + APP_MODE;
const NATIVE_REDIRECT_URI = `${NATIVE_REDIRECT_SCHEME}://oauth2redirect`;
const LOGOUT_PENDING_KEY = 'ny_logout_pending';
const FORCE_LOGIN_PROMPT_KEY = 'ny_force_login_prompt';

function isNative() {
    return Capacitor.isNativePlatform();
}

/*  TARTÓS BELÉPÉS: a telepített alkalmazás (natív mobil ÉS asztali program) újraindítás után is
 *  bent tartja a felhasználót (user, 2026-10-07).
 *
 *  A webes út (a Keycloak munkamenet-sütijével, rejtett iframe-ben frissítő token) az asztali
 *  programban nem tart: a munkamenet-süti a program bezárásakor elvész, így minden indításnál újra
 *  be kellett lépni. Itt ezért – mint a mobilon – `offline_access` frissítő jegyet kérünk; az a
 *  helyi tárban (localStorage, az asztali programban is megmarad) túléli az újraindítást, és vele
 *  csendben új belépési jegy kérhető. Kijelentkezéskor törlődik (clearClientAuthStorage). */
function tartosBelepes() {
    return isNative() || !!(window as any).CRONOM_ASZTALI;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
    /**
     * Indulási hiba, ha az OIDC discovery nem töltődött be.
     *
     * Nem `throw`, mert ez `APP_INITIALIZER`-ből fut: a dobott hiba az egész
     * alkalmazás indulását megakadályozná, és a felhasználó üres képernyőt
     * kapna. A hiba itt megmarad, és a felület kiírja.
     */
    indulasiHiba: string | null = null;

    private config: AuthConfig = {
        //  OIDC-issuer: ha az environment.oidcIssuer adott (Entra External ID / más OIDC IdP), a TELJES
        //  issuer és kliens-ID; üresen a Keycloak-alapértelmezés (${keycloak}/realms/master, 'nyilvantarto').
        issuer: (environment as any).oidcIssuer || `${environment.keycloak}/realms/master`,
        clientId: (environment as any).oidcClientId || 'nyilvantarto',
        responseType: 'code',
        scope: 'openid profile email' + (tartosBelepes() ? ' offline_access' : ''),

        redirectUri: isNative()
            ? NATIVE_REDIRECT_URI
            : window.location.origin + '/',
        postLogoutRedirectUri: isNative()
            ? NATIVE_REDIRECT_URI
            : window.location.origin + '/',

        // WEB: a rejtett token-frissítő iframe céllapja.
        //
        // Enélkül a könyvtár alapértéke üres, és a `redirectUri`-ra esik
        // vissza (`silentRefreshRedirectUri || redirectUri`) – vagyis az
        // alkalmazás gyökerére. Az iframe így nem egy pár soros visszajelző
        // lapot tölt be, hanem a TELJES alkalmazást; az ott újrainduló
        // példány pedig saját frissítő iframe-et nyit. A böngészőfül ettől
        // vég nélkül tölt, a szülő pedig 20 másodperc után
        // `silent_refresh_timeout`-tal elbukik.
        //
        // Natívon nincs iframe-es frissítés (offline_access refresh_token),
        // ezért ott üresen hagyjuk – a mező ott nem is kerül használatra.
        silentRefreshRedirectUri: isNative()
            ? ''
            : window.location.origin + '/assets/silent-refresh.html',

        // IMPORTANT: for mobile, open system/in-app browser instead of window.location
        openUri: async (uri: string) => {
            if (isNative()) {
                await Browser.open({ url: uri });
            } else {
                window.location.href = uri;
            }
        },

        // Helps when you want to return to the originally requested route after login:
        preserveRequestedRoute: true,

        // Default is 'remoteOnly' (http allowed only for localhost). On LAN IP live-reload it will be treated as "remote".
        // You can keep the default in prod; for dev you may temporarily set false (see notes below).
        // requireHttps: 'remoteOnly',
        showDebugInformation: false,
        requireHttps: false,
        //  Entra External ID: a discovery-végpontjai (…/oauth2/v2.0/…) NEM az issuer-prefixen vannak,
        //  ezért a szigorú ellenőrzés elbukna ("Every url in discovery document has to start with …" →
        //  „az azonosítószolgáltató nem érhető el"). Entránál (van oidcIssuer) ezért kikapcsoljuk;
        //  Keycloaknál marad a szigorú (változatlan viselkedés).
        strictDiscoveryDocumentValidation: (environment as any).oidcIssuer ? false : true
    };

    constructor(private oauth: OAuthService, private router: Router) { }

    // ---- Több-intézményes (decentralizált) belépés: melyik intézmény IdP-jéhez lépünk be ----
    // A választott intézmény deploy-domainjét (baseDomain) localStorage-ban tartjuk; az index.html
    // ezt MÉG az Angular bundle előtt beteszi window.env.baseDomain-be → az environment az adott
    // intézmény IdP/API-jához derivál. Web: a host már megmondja az intézményt → nem kell választó.
    private readonly INST_BASE_KEY = 'ny_intezmeny_baseDomain';
    private readonly INST_IDP_KEY = 'ny_intezmeny_idp';
    private readonly INST_META_KEY = 'ny_intezmeny_meta';
    hasChosenInstitution(): boolean {
        try { return !!localStorage.getItem(this.INST_BASE_KEY); } catch { return false; }
    }
    chosenInstitution(): any {
        try { return JSON.parse(localStorage.getItem(this.INST_META_KEY) || 'null'); } catch { return null; }
    }
    // A választó menti ezt, majd újratölt – az újratöltés után az index.html a meta-ból MINDEN
    // szolgáltatás-URL-t (idp/api/avatars/forum/n8n) a window.env-be tesz, mielőtt az environment kiértékel.
    chooseInstitution(inst: { id?: string; name?: string; baseDomain: string; idp?: string; api?: string; avatars?: string; forum?: string; n8n?: string; logo?: string }): void {
        try {
            localStorage.setItem(this.INST_BASE_KEY, String(inst.baseDomain || '').replace(/^https?:\/\//, '').replace(/\/+$/, ''));
            localStorage.setItem(this.INST_META_KEY, JSON.stringify(inst));
            localStorage.removeItem(this.INST_IDP_KEY); // régi külön kulcs – ma a meta.idp-t használjuk
        } catch { /* ignore */ }
    }
    // „Másik intézmény" – a választás + a teljes munkamenet törlése (a hívó ezután újratölt/választóra visz).
    clearInstitution(): void {
        try {
            localStorage.removeItem(this.INST_BASE_KEY);
            localStorage.removeItem(this.INST_IDP_KEY);
            localStorage.removeItem(this.INST_META_KEY);
        } catch { /* ignore */ }
    }

    // Egyszemélyes (Keycloak nélküli) mód – a környezetből vezérelve.
    isStandalone(): boolean {
        return !!(environment as any).standalone;
    }
    // Standalone-on belül: helyi username+jelszó kapu (vs fix user).
    isStandaloneLogin(): boolean {
        return !!(environment as any).standalone && !!(environment as any).standaloneLogin;
    }
    get standaloneUser(): string {
        return String((environment as any).standaloneUser || 'local-admin').trim().toLowerCase();
    }

    // ---- Helyi (standalone) token kezelése ----
    // A tokent sessionStorage-ban tartjuk: futás (és oldalfrissítés) közben megmarad, de az
    // alkalmazás BEZÁRÁSAKOR automatikusan törlődik → újraindításkor újra be kell jelentkezni.
    // (A token TTL-je hosszú a backend oldalon, így használat közben nem jár le.)
    private readonly LOCAL_TOKEN_KEY = 'ny_standalone_token';
    getLocalToken(): string | null {
        try { return sessionStorage.getItem(this.LOCAL_TOKEN_KEY); } catch { return null; }
    }
    setLocalToken(token: string): void {
        try { sessionStorage.setItem(this.LOCAL_TOKEN_KEY, token); } catch { /* ignore */ }
    }
    clearLocalToken(): void {
        try { sessionStorage.removeItem(this.LOCAL_TOKEN_KEY); } catch { /* ignore */ }
        try { localStorage.removeItem(this.LOCAL_TOKEN_KEY); } catch { /* ignore */ } // régi (localStorage) tárhely takarítása
    }
    private decodeJwt(token: string): any {
        try {
            const payload = token.split('.')[1];
            return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
        } catch { return null; }
    }
    hasValidLocalToken(): boolean {
        const t = this.getLocalToken();
        if (!t) return false;
        const p = this.decodeJwt(t);
        if (!p || !p.exp) return false;
        return p.exp * 1000 > Date.now();
    }
    localUsername(): string {
        const p = this.getLocalToken() ? this.decodeJwt(this.getLocalToken()!) : null;
        return String(p?.preferred_username || '').trim().toLowerCase();
    }

    /*  STANDALONE: A KÉPERNYŐK PROFIL-LEKÉRÉSE. Sok képernyő (szülői jegyek, órarend,
     *  étkezés, naptár…) az ngOnInit elején `await this.oauth.loadUserProfile()`-t hív. OIDC
     *  nélkül ez kivételt dob („nincs access token”), és a képernyő ott megáll: nem tölt be
     *  semmit. Standalone módban ezért a profilt a HELYI felhasználóból adjuk – ugyanazzal
     *  az alakkal, amit a Keycloak userinfo adna. */
    private standaloneProfil(): void {
        const profil = () => {
            const u = this.isStandaloneLogin() ? this.localUsername() : this.standaloneUser;
            return { preferred_username: u, username: u, sub: u };
        };
        const o: any = this.oauth;
        o.loadUserProfile = async () => ({ info: profil() });
        o.getIdentityClaims = () => profil();
    }

    async init(): Promise<void> {
        // Standalone módban nincs Keycloak: nem konfigurálunk OIDC-t, nincs discovery/redirect.
        if (this.isStandalone()) {
            console.log('[auth] STANDALONE mód – Keycloak kihagyva, helyi felhasználó:', this.standaloneUser);
            this.standaloneProfil();
            return;
        }

        // 1) If native, listen for the redirect back into the app (custom scheme / universal link).
        // Capacitor fires appUrlOpen for these events. :contentReference[oaicite:9]{index=9}
        if (isNative()) {
            App.addListener('appUrlOpen', async ({ url }) => {
                // Példa: hu.cronomit.sulinform://oauth2redirect?code=...&state=...
                // CSAK a saját OAuth-visszairányításunkat kezeljük – az egyéb mély-linkeket
                // (pl. push-értesítés megnyitása) hagyjuk békén, különben feleslegesen token-cserét indítanánk.
                if (!url || url.indexOf(`${NATIVE_REDIRECT_SCHEME}://`) !== 0) return;

                // A FITNESS külső-forrás csatlakozás (Polar stb.) UGYANEZT a custom scheme-et
                // használja, mert a szolgáltatóknál ez van bejegyezve. Az ő state-jük `fc_`
                // előtaggal jön – azt a ConnectService kezeli, mi ne nyeljük le.
                if (/[?&]state=fc_/.test(url)) return;

                const qs = url.split('?')[1] ?? '';

                // A böngésző-fület MINDEN visszairányításnál zárjuk (bejelentkezés ÉS kijelentkezés is ide tér vissza).
                await Browser.close().catch(() => { });

                // 'code' nélkül ez logout- vagy hiba-visszatérés → nincs mit tokenre cserélni.
                if (!/[?&]code=/.test(url)) return;

                // angular-oauth2-oidc tryLoginCodeFlow a customHashFragment-ből olvassa a code/state-et
                // (levágja az első karaktert, a maradékot query-ként értelmezi).
                try {
                    await this.oauth.tryLoginCodeFlow({
                        customHashFragment: '#' + qs,
                        preventClearHashAfterLogin: true,
                        customRedirectUri: NATIVE_REDIRECT_URI,
                    });

                    if (this.oauth.hasValidAccessToken()) {
                        window.location.reload();
                    }
                } catch (e) {
                    console.error('tryLoginCodeFlow failed', e);
                }
            });
        }

        // 2) Normal library bootstrap: configure + discovery + attempt to login from redirect
        this.oauth.configure(this.config);
        if (typeof localStorage !== 'undefined') {
            this.oauth.setStorage(localStorage);
            (this.oauth as any).saveNoncesInLocalStorage = true;
        }
        // A DISCOVERY HIBÁJA NEM DÖNTHETI ROMBA AZ INDULÁST.
        //
        // Ez egy `APP_INITIALIZER`. Ha a promise ELUTASÍTÁSSAL zárul, az Angular
        // el sem indítja az alkalmazást: nem lesz `app-root`, nem lesz hibaüzenet,
        // nem lesz gomb – csak egy fehér lap. Márpedig ez a hívás hálózatfüggő:
        // elég, ha az azonosítószolgáltató épp újraindul, lassan válaszol, vagy a
        // proxy egy pillanatra elesik.
        //
        // Ezért a hibát ELKAPJUK és MEGJEGYEZZÜK: az app elindul, és meg tudja
        // mondani a felhasználónak, mi történt. A hibát nem nyeljük el – naplózzuk,
        // és a felület kiírja.
        try {
            //  IDŐKORLÁT (2026-10-09, Google Play-jelzés: „fehér oldal, nem megy tovább"). Ez az
            //  APP_INITIALIZER: amíg a promise függ, az Angular el sem indul, a képernyő FEHÉR.
            //  Elutasító hálózaton a hiba gyorsan jön, de LÓGÓ hálózaton (elnyelt csomagok, lassú
            //  proxy, tesztlabor) a WebView percekig várhat – ezért 15 mp után feladjuk, és a
            //  felület a hibaüzenetet és az „újra” gombot mutatja.
            await Promise.race([
                this.oauth.loadDiscoveryDocumentAndTryLogin(),
                new Promise((_, elutasit) => setTimeout(() => elutasit(new Error('időtúllépés (discovery)')), 15000)),
            ]);
            this.indulasiHiba = null;
        } catch (e: any) {
            this.indulasiHiba = 'Az azonosítószolgáltató most nem érhető el, ezért a '
                + 'bejelentkezés nem tudott elindulni.';
            console.error('[auth] a discovery dokumentum betöltése nem sikerült', e);
            return;   // silent refresh sincs értelme discovery nélkül
        }

        if (tartosBelepes()) {
            // NATÍV és ASZTALI: NINCS iframe-es silent refresh (WebView-ben nem működik → ettől „dobta ki" a usert).
            // Helyette offline_access refresh_token flow. Hidegindításkor, ha lejárt az access token de van
            // refresh token, azonnal frissítünk → a felhasználó BENT marad újraindítás után is.
            //  A frissítő jegy fajtája: „Offline” = tartós (a Keycloak munkamenetétől független); ha a
            //  Keycloak nem ad ilyet (a felhasználónak nincs offline_access szerepe, vagy a kliensnél nincs
            //  engedve), csak a központi munkamenet idejéig él – ezt a naplóba írjuk, hogy kideríthető legyen.
            const rt = this.oauth.getRefreshToken();
            if (rt) {
                let typ = '';
                try { typ = JSON.parse(atob(rt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).typ || ''; } catch { /* nem JWT */ }
                if (typ && typ !== 'Offline') console.warn(`[auth] a frissítő jegy nem tartós (typ=${typ}) – a Keycloak nem ad offline_access-t ennek a felhasználónak/kliensnek`);
            }
            try {
                if (!this.oauth.hasValidAccessToken() && rt) {
                    await this.oauth.refreshToken();
                }
            } catch (e: any) {
                /*  A FRISSÍTÉS NEM SIKERÜLT (lejárt / visszavont frissítő jegy). Eddig a lejárt jeggyel
                 *  indult tovább a program: minden kérés 401/403-at kapott, és úgy tűnt, a kiszolgáló
                 *  tilt (user, 2026-10-07). Most a halott jegyeket töröljük – a beléptető őr a
                 *  bejelentkezésre visz. */
                console.warn('[auth] hidegindítású refresh nem sikerült – újra be kell lépni', e?.error || e);
                try { this.oauth.logOut(true); } catch { /* csak helyi törlés */ }
            }
            // Lejárat ELŐTT is refresh_token-nel frissítünk (nem iframe-mel).
            this.oauth.events
                .pipe(filter((e: any) => e.type === 'token_expires'))
                .subscribe(() => { this.tartosFrissit(); });
            //  ALVÁS / HÁTTÉR UTÁN: a gép elalvásakor (vagy a háttérben) a lejárat előtti időzítő nem fut le,
            //  így a jegy lejárva marad. Amikor az ablak újra előtérbe kerül, azonnal frissítünk.
            const ebredes = () => { if (document.visibilityState !== 'hidden' && !this.oauth.hasValidAccessToken()) this.tartosFrissit(); };
            document.addEventListener('visibilitychange', ebredes);
            window.addEventListener('focus', ebredes);
            window.addEventListener('online', ebredes);
        } else {
            /*  LEJÁRT TOKENNEL NE INDULJON AZ ALKALMAZÁS.
             *
             *  Ha a böngészőben előző alkalomról maradt munkamenet, a token induláskor már
             *  lejárt. Az automatikus frissítés ugyan elindul, de az alkalmazás közben
             *  felépül: a belépés utáni lekérések (saját adatok, jogkör, menü) még a lejárt
             *  tokennel mennek ki, a kiszolgáló „csak az iskola munkatársa érheti el”-lel
             *  elutasítja őket, és az admin-jog/menü a következő frissítésig hiányzik
             *  (europa2000, Libus Ágnes, 2026-09-28: „először nem adja meg az admin jogot,
             *  csak ha frissítek”). Ezért itt – az alkalmazás indulása ELŐTT – egyszer
             *  csendben frissítünk, rövid határidővel. Ha nem sikerül (lejárt a központi
             *  munkamenet is), minden marad a régiben: a beléptető őr átirányít. */
            if (!this.oauth.hasValidAccessToken() && this.oauth.getAccessToken()) {
                try {
                    await Promise.race([
                        this.oauth.silentRefresh(),
                        new Promise((_, elutasit) => setTimeout(() => elutasit(new Error('időtúllépés')), 8000)),
                    ]);
                } catch (e) {
                    console.warn('[auth] induláskori token-frissítés nem sikerült', e);
                }
            }
            // WEB: marad az iframe-es silent refresh.
            this.oauth.setupAutomaticSilentRefresh();
        }
    }

    /*  TARTÓS (offline_access) JEGY FRISSÍTÉSE – EGYSZERRE CSAK EGY.
     *
     *  A Keycloak minden frissítéskor ÚJ frissítő jegyet ad, és a régit érvényteleníti. Ha két frissítés
     *  futna egyszerre (az időzítő, az előtérbe kerülés és egy kérés egyszerre vette észre a lejáratot),
     *  a második a már elhasznált jeggyel menne, és a munkamenet elveszne („nem tudja betölteni az
     *  ügyfeleket”, user 2026-10-07). Ezért a futó frissítést mindenki megvárja. Ha a frissítés végleg nem
     *  sikerül (a jegyet visszavonták / lejárt), a halott jegyeket töröljük és a belépés jön – nem marad
     *  félig működő, mindenre 401-et kapó állapot. Visszatér: sikerült-e. */
    private frissitesFut: Promise<boolean> | null = null;
    tartosFrissit(): Promise<boolean> {
        if (!tartosBelepes() || this.isStandalone()) return Promise.resolve(this.oauth.hasValidAccessToken());
        if (this.frissitesFut) return this.frissitesFut;
        if (!this.oauth.getRefreshToken()) return Promise.resolve(this.oauth.hasValidAccessToken());
        this.frissitesFut = (async () => {
            for (let proba = 0; proba < 2; proba++) {
                try { await this.oauth.refreshToken(); return true; }
                catch (e: any) {
                    const vegleges = e?.status === 400 || e?.status === 401 || /invalid_grant/.test(JSON.stringify(e?.error || ''));
                    console.warn('[auth] a jegy frissítése nem sikerült', e?.status, e?.error || e);
                    if (vegleges) break;
                    await new Promise((r) => setTimeout(r, 1500));      // hálózati hiba (ébredés után): még egyszer
                }
            }
            if (!navigator.onLine) return false;                       // nincs net: majd az 'online' eseménynél
            try { this.oauth.logOut(true); } catch { /* csak helyi törlés */ }
            try { this.login(); } catch { /* a beléptető őr úgyis odavisz */ }
            return false;
        })().finally(() => { this.frissitesFut = null; });
        return this.frissitesFut;
    }

    login(): void {
        if (this.isStandalone()) return; // standalone: nincs OIDC login
        if (this.oauth.hasValidAccessToken()) return;

        // Natív, több-intézményes: OAuth előtt KELL a intézmény (IdP) kiválasztása.
        if (isNative() && !this.hasChosenInstitution()) {
            try { this.router.navigateByUrl('/intezmeny-belepo'); } catch { /* ignore */ }
            return;
        }

        const forcePrompt = this.shouldForceLoginPrompt();
        const params = forcePrompt
            ? { prompt: 'login', max_age: '0' }
            : {};

        this.clearForceLoginPrompt();
        this.oauth.initCodeFlow('', params);
    }

    /**
     *  Kijelentkezés.
     *
     *  ALAPÉRTELMEZÉSBEN CSAK EBBŐL A RENDSZERBŐL léptet ki. Korábban a webes ág a
     *  Keycloak `end_session` végpontjára irányított, ami megszünteti a KÖZPONTI
     *  munkamenetet is – és ha a belépés föderált azonosítóval (Microsoft-fiókkal)
     *  történt, a kijelentkezés továbbgyűrűzött oda is: a felhasználó a levelezéséből
     *  és minden más Microsoft-alkalmazásból is kiesett. Ez nem az, amit a
     *  „Kijelentkezés" gombtól várni lehet.
     *
     *  Most a helyi munkamenetet zárjuk: töröljük a tokeneket és a tárolt adatokat.
     *  A központi munkamenet érintetlen marad, DE a következő belépés `prompt=login`
     *  paraméterrel indul (lásd `login()`), tehát a rendszer akkor is rákérdez, ki
     *  lép be – nem lépteti vissza némán ugyanazt a felhasználót.
     *
     *  `mindenhonnan = true` esetén marad a régi, teljes (SSO-) kijelentkezés – ez
     *  kell például közös gépnél, ahol tényleg mindenhonnan ki akarunk lépni.
     */
    logout(mindenhonnan = false): void {
        //  A nyitott szerkesztési zárak azonnali elengedése (szerk-zar.service.ts) – MÉG a tárolók ürítése
        //  előtt, mert az elengedő kérés a tokennel megy. Esemény, hogy ne legyen körkörös függés.
        try { window.dispatchEvent(new Event('cronom-kilepes')); } catch (e) { /* ignore */ }
        if (this.isStandalone()) {
            // Standalone: nincs Keycloak munkamenet – csak a helyi tárolót ürítjük és újratöltünk.
            try { localStorage.clear(); sessionStorage.clear(); } catch (e) { /* ignore */ }
            window.location.href = window.location.origin + '/';
            return;
        }
        const idToken = this.oauth.getIdToken();
        const postLogoutRedirectUri = this.config.postLogoutRedirectUri as string;
        const logoutUrl = this.buildLogoutUrl(idToken, postLogoutRedirectUri);

        try {
            sessionStorage.setItem(LOGOUT_PENDING_KEY, '1');
            localStorage.setItem(LOGOUT_PENDING_KEY, '1');
            sessionStorage.setItem(FORCE_LOGIN_PROMPT_KEY, '1');
            localStorage.setItem(FORCE_LOGIN_PROMPT_KEY, '1');
        } catch (error) {
            console.warn('Failed to mark logout as pending', error);
        }

        this.clearClientAuthStorage();

        if (isNative()) {
            // Natív: TELJES kijelentkezés + vissza az intézményválasztóra. A helyi tokeneket (access + id +
            // a perzisztens REFRESH token) töröljük – e nélkül a következő indításkor a refresh token
            // visszaléptetné a felhasználót. A választott intézményt is töröljük, és a gyökérre töltünk újra
            // → a landing/guard az intézményválasztóra visz (nincs token + nincs intézmény).
            try { this.oauth.logOut(true); } catch (e) { /* csak a helyi tokeneket üríti, nincs redirect */ }
            this.clearInstitution();
            try { window.location.href = '/'; } catch (e) { /* ignore */ }
            return;
        }

        if (mindenhonnan && logoutUrl) {
            this.openLogoutUrl(logoutUrl);
            return;
        }

        // Csak helyi kijelentkezés: a tokeneket töröljük átirányítás nélkül, majd a
        // gyökérre töltünk újra – onnan a guard a bejelentkezésre visz.
        try { this.oauth.logOut(true); } catch (e) { /* csak a helyi tokeneket üríti */ }
        try { window.location.href = window.location.origin + '/'; } catch (e) { /* ignore */ }
    }

    private buildLogoutUrl(idToken: string, postLogoutRedirectUri: string): string {
        const params = new URLSearchParams({
            client_id: this.config.clientId as string,
            post_logout_redirect_uri: postLogoutRedirectUri,
            redirect_uri: postLogoutRedirectUri,
        });

        if (idToken) {
            params.set('id_token_hint', idToken);
        }

        //  Az end_session végpont az OIDC discovery-ből (a library a `logoutUrl`-be tölti) – ez Keycloaknál
        //  és Entra External ID-nél is a helyes cím. Tartalékként: ha van oidcIssuer (Entra), annak a logout
        //  címe; különben a Keycloak end_session.
        const felfedezett = (this.oauth as any).logoutUrl as string | undefined;
        const tartalek = (environment as any).oidcIssuer
            ? `${String((environment as any).oidcIssuer).replace(/\/+$/, '')}/oauth2/v2.0/logout`
            : `${environment.keycloak}/realms/master/protocol/openid-connect/logout`;
        return `${felfedezett || tartalek}?${params.toString()}`;
    }

    private openLogoutUrl(url: string): void {
        if (isNative()) {
            Browser.open({ url }).catch(error => {
                console.warn('Failed to open logout URL in native browser', error);
                window.location.href = url;
            });
            return;
        }

        window.location.href = url;
    }

    private clearClientAuthStorage(): void {
        this.clearOauthStorage(sessionStorage);
        this.clearOauthStorage(localStorage);
    }

    private clearOauthStorage(storage: Storage): void {
        try {
            const keysToRemove: string[] = [];

            for (let i = 0; i < storage.length; i++) {
                const key = storage.key(i);
                if (!key || key === LOGOUT_PENDING_KEY || key === FORCE_LOGIN_PROMPT_KEY) continue;

                const normalizedKey = key.toLowerCase();
                if (
                    normalizedKey.includes('oauth')
                    || normalizedKey.includes('oidc')
                    || normalizedKey.includes('token')
                    || normalizedKey.includes('nonce')
                    || normalizedKey.includes('pkce')
                    || normalizedKey.includes('code_verifier')
                    || normalizedKey.includes('auth')
                ) {
                    keysToRemove.push(key);
                }
            }

            keysToRemove.forEach(key => storage.removeItem(key));
        } catch (error) {
            console.warn('Failed to clear OAuth storage', error);
        }
    }

    private shouldForceLoginPrompt(): boolean {
        try {
            return sessionStorage.getItem(FORCE_LOGIN_PROMPT_KEY) === '1'
                || localStorage.getItem(FORCE_LOGIN_PROMPT_KEY) === '1';
        } catch (error) {
            return false;
        }
    }

    private clearForceLoginPrompt(): void {
        try {
            sessionStorage.removeItem(FORCE_LOGIN_PROMPT_KEY);
            localStorage.removeItem(FORCE_LOGIN_PROMPT_KEY);
        } catch (error) {
            console.warn('Failed to clear forced login prompt flag', error);
        }
    }

    get accessToken(): string {
        return this.oauth.getAccessToken();
    }

    get isLoggedIn(): boolean {
        return this.oauth.hasValidAccessToken();
    }
}
